function scriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/&/g, "\\u0026");
}

type SourceToken = { start: number; end: number; value: string; kind: "string" | "code" };

// Inspect executable tokens only. Replacing location text globally also changes
// strings, comments, regular expressions and generated JSX text.
function sourceTokens(source: string): SourceToken[] {
  const tokens: SourceToken[] = [];
  for (let i = 0; i < source.length;) {
    const start = i;
    const char = source[i];
    if (/\s/.test(char)) { i += 1; continue; }
    if (source.startsWith("//", i)) { i = source.indexOf("\n", i); if (i < 0) break; continue; }
    if (source.startsWith("/*", i)) { const end = source.indexOf("*/", i + 2); i = end < 0 ? source.length : end + 2; continue; }
    if (char === '"' || char === "'" || char === "`") {
      i += 1;
      while (i < source.length) {
        if (source[i] === "\\") { i += 2; continue; }
        if (source[i++] === char) break;
      }
      tokens.push({ start, end: i, value: source.slice(start + 1, i - 1), kind: "string" });
      continue;
    }
    const previous = tokens[tokens.length - 1]?.value;
    if (char === "/" && (!previous || ["=", "(", ":", ",", ";", "!", "?", "{", "[", "|", "&", "return", "throw", "case", "yield"].includes(previous))) {
      i += 1;
      let characterClass = false;
      while (i < source.length) {
        if (source[i] === "\\") { i += 2; continue; }
        if (source[i] === "[") characterClass = true;
        if (source[i] === "]") characterClass = false;
        if (source[i++] === "/" && !characterClass) break;
      }
      while (/[a-z]/i.test(source[i] || "") && i < source.length) i += 1;
      tokens.push({ start, end: i, value: "<regexp>", kind: "string" });
      continue;
    }
    const identifier = source.slice(i).match(/^[A-Za-z_$][\w$]*/)?.[0];
    if (identifier) i += identifier.length;
    else {
      const operator = source.slice(i).match(/^(?:===|!==|==|!=|=>|\?\.|\+\+|--|&&|\|\|)/)?.[0];
      i += operator?.length || 1;
    }
    tokens.push({ start, end: i, value: source.slice(start, i), kind: "code" });
  }
  return tokens;
}

export function rewriteRuntimeNavigationSource(source: string, proxyBaseUrl: string): string {
  const tokens = sourceTokens(source);
  const edits: { start: number; end: number; value: string }[] = [];
  const isModuleSpecifier = (index: number) => ["from", "import"].includes(tokens[index - 1]?.value)
    || tokens[index - 1]?.value === "(" && tokens[index - 2]?.value === "import";
  const reactImport = tokens.find((token, i) => token.kind === "string" && isModuleSpecifier(i)
    && token.value.startsWith(`${proxyBaseUrl}/`) && /\/react\.js(?:[?#]|$)/.test(token.value))?.value;
  const hasLocalLocation = tokens.some((token, i) => token.value === "location"
    && (["const", "let", "var", "import", "function", "class"].includes(tokens[i - 1]?.value)
      // Conservatively leave bare Location access alone when a parameter,
      // destructuring binding, or import can introduce a local identifier.
      || ["(", ",", "{", ":", "as"].includes(tokens[i - 1]?.value) && [",", ")", "}", "="].includes(tokens[i + 1]?.value)));
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if (token.kind === "string" && isModuleSpecifier(i)
      && /\/react-router(?:-dom)?\.js(?:[?#]|$)/.test(token.value) && token.value.startsWith(`${proxyBaseUrl}/`)
      && !token.value.includes("#applyt-native-router")) {
      const reactUrl = reactImport || token.value.replace(/react-router(?:-dom)?(?=\.js(?:[?#]|$))/, "react");
      const adapter = `data:text/javascript;charset=utf-8,${encodeURIComponent(runtimeRouterModule(token.value, reactUrl))}`;
      edits.push({ start: token.start, end: token.end, value: JSON.stringify(adapter) });
      continue;
    }
    if (token.kind !== "code" || tokens[i - 1]?.value === ".") continue;
    const explicit = ["window", "globalThis", "document"].includes(token.value)
      && tokens[i + 1]?.value === "." && tokens[i + 2]?.value === "location";
    const bare = token.value === "location" && !hasLocalLocation;
    if (!explicit && !bare) continue;
    const last = explicit ? i + 2 : i;
    const next = tokens[last + 1]?.value;
    const method = tokens[last + 2]?.value;
    const mutation = next === "." && (["assign", "replace"].includes(method) && tokens[last + 3]?.value === "("
      || ["href", "pathname", "search", "hash"].includes(method) && tokens[last + 3]?.value === "=");
    if (mutation) {
      const original = source.slice(token.start, tokens[last].end);
      edits.push({ start: token.start, end: tokens[last].end, value: `(window.__APPLYT_RUNTIME_NAVIGATION__||${original})` });
    } else if (next === "=" && (bare || token.value !== "document")) {
      const owner = bare ? "window" : token.value;
      edits.push({ start: token.start, end: tokens[last].end, value: `(window.__APPLYT_RUNTIME_NAVIGATION__||${owner}).location` });
    }
  }
  for (const edit of edits.sort((a, b) => b.start - a.start)) source = source.slice(0, edit.start) + edit.value + source.slice(edit.end);
  return source;
}

// The original dependency uses a distinct fragment so the import map does not
// redirect its own re-export back into this adapter. All public hooks and Links
// still share the original router's context and React instance.
export function runtimeRouterModule(routerUrl: string, reactUrl: string): string {
  const nativeRouterUrl = `${routerUrl}#applyt-native-router`;
  return `import React from ${scriptJson(reactUrl)};
import * as R from ${scriptJson(nativeRouterUrl)};
export * from ${scriptJson(nativeRouterUrl)};
export function MemoryRouter(props){
  var api=window.__APPLYT_RUNTIME_NAVIGATION__;
  if(!api)return React.createElement(R.MemoryRouter,props);
  api.initialize(props);
  var pair=React.useState(api.current),current=pair[0],setCurrent=pair[1];
  React.useLayoutEffect(function(){setCurrent(api.current());return api.subscribe(setCurrent);},[api]);
  return React.createElement(R.Router,{basename:props.basename,location:current.location,navigationType:current.action,navigator:api.navigator,future:props.future},props.children);
}
export var BrowserRouter=MemoryRouter;
export var HashRouter=MemoryRouter;
export function createMemoryRouter(routes,options){
  var api=window.__APPLYT_RUNTIME_NAVIGATION__;
  if(!api)return R.createMemoryRouter(routes,options);
  api.initialize(options);
  var seed=api.snapshot();
  var router=R.createMemoryRouter(routes,Object.assign({},options,{initialEntries:seed.entries,initialIndex:seed.index}));
  var originalNavigate=router.navigate.bind(router);
  var detach=api.attach(function(to,opts){originalNavigate(to,opts);});
  var unsubscribe=router.subscribe(function(state){if(state.initialized&&!state.navigation.location)api.commit(state.location,state.historyAction);});
  router.navigate=function(to,opts){return typeof to==='number'?Promise.resolve(api.navigator.go(to)):originalNavigate(to,opts);};
  var dispose=router.dispose.bind(router);
  router.dispose=function(){unsubscribe();detach();dispose();};
  return router;
}
export var createBrowserRouter=createMemoryRouter;
export var createHashRouter=createMemoryRouter;
export default Object.assign({},R,{MemoryRouter,BrowserRouter,HashRouter,createMemoryRouter,createBrowserRouter,createHashRouter});`;
}
